import {
  Badge,
  Chip,
  IconButton,
  TextField,
  Tooltip,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import ContactsIcon from '@mui/icons-material/Contacts';
import MailIcon from '@mui/icons-material/Mail';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import CloudIcon from '@mui/icons-material/Cloud';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ClearIcon from '@mui/icons-material/Clear';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../global/theme/tokens';
import ExoForm from '../exo/ExoForm';
import { SettingsPopover } from '../exo/project/SettingsPopover';
import ExoDialog from '../exo/ExoDialog';
import { SelectFromNameValueArray } from './SelectFromNameValueArray';
import Feedback from '../special/Feedback';
import { ExoAutoCompleteSelector } from '../exo/ExoAutoCompleteSelector';
import ExoAvatar from '../exo/ExoAvatar';
import ExoExplorer from '../exo/ExoExplorer';
import MediaPreview from '../exo/explorer/MediaPreview';

const MAX_ATTACHMENT_SIZE_KB = 8 * 1024;

const SelectDelivery = ({
  onSelect,
  recipient,
  useInvoiceMail,
  defaultType = 'recipientMail',
  options = ['recipientMail', 'contactMail', 'customMail', 'saveOnly'],
  allowCC,
  allowMailContent,
  defaultRecipientMailKey,
  deliveryObj,
  textBlockType,
  allowAttachments = true,
  startData,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const [type, setType] = useState('');

  const [nameValueData, setNameValueData] = useState({});
  const [deliveryObject, setDeliveryObject] = useState(deliveryObj || {});

  const [attachments, setAttachments] = useState(
    deliveryObj?.attachments || startData?.attachments || null,
  );

  useEffect(() => {
    setDeliveryObject(deliveryObj || {});
    if (deliveryObj?.attachments !== undefined)
      setAttachments(deliveryObj.attachments);
  }, [deliveryObj]);

  // keeps the attachments on every delivery change, regardless of the parent
  function emit(data) {
    onSelect(allowAttachments ? { ...data, attachments } : data);
  }

  useEffect(() => {
    if (type === '')
      setType(deliveryObj ? deliveryObj.defaultType : defaultType);
  }, [deliveryObj]);

  function handleDelivertype(event, newType) {
    if (newType) setType(newType);
    if (newType === 'contactMail') {
      emit({
        ...deliveryObject,
        saveOnly: false,
        defaultType: 'contactMail',
      });
    }
    if (newType === 'customMail') handleCustomMail({});
    if (newType === 'recipientMail') {
      handleRecipientMail(
        recipient?.emails && recipient?.emails.length >= 1
          ? recipient?.emails[0]?.emailAddress
          : null,
      );
    }
    if (newType === 'saveOnly') {
      handleSaveOnly();
    }
  }

  const optionMap = {
    recipientMail: {
      icon: <ContactMailIcon />,
      label: t('Default Mail'),
      value: 'recipientMail',
    },
    contactMail: {
      icon: <ContactsIcon />,
      label: t('Contact Mail'),
      value: 'contactMail',
    },
    customMail: {
      icon: <AlternateEmailIcon />,
      label: t('Custom Mail'),
      value: 'customMail',
    },
    saveOnly: {
      icon: <CloudIcon />,
      label: t('Save Only'),
      value: 'saveOnly',
    },
  };

  // handle callback
  function handleRecipientMail(deliveryMail) {
    emit({
      ...deliveryObject,
      saveOnly: false,
      id: null,
      deliveryMail: deliveryMail,
      defaultType: 'recipientMail',
      deliveryName:
        recipient &&
        (recipient.name
          ? recipient.name
          : `${recipient.salutation} ${recipient.firstName} ${recipient.lastName}`),
    });
  }
  function handleCustomMail(newCustom) {
    emit({
      ...deliveryObject,
      id: null,
      saveOnly: false,
      defaultType: 'customMail',
      deliveryMail: newCustom.email,
      deliveryName: newCustom.name,
    });
  }
  function handleContactMail(data, type) {
    // Extract name based on entity type
    const nameMap = {
      company: (d) => d.nameShort || d.name,
      companyLead: (d) => d.nameShort || d.name,
      contact: (d) =>
        `${d.salutation || ''} ${d.firstName || ''} ${d.lastName || ''}`.trim(),
      personLead: (d) =>
        `${d.salutation || ''} ${d.firstName || ''} ${d.lastName || ''}`.trim(),
      user: (d) => `${d.firstName || ''} ${d.lastName || ''}`.trim(),
    };

    const deliveryName = nameMap[type]
      ? nameMap[type](data)
      : data.name || data.email || '';

    // Extract email(s) based on entity type
    let emails = [];
    if (type === 'user') {
      if (data.email)
        emails = [{ emailType: 'work', emailAddress: data.email }];
    } else if (Array.isArray(data.emails)) {
      emails = data.emails;
    }

    if (emails.length === 0) {
      emit({ saveOnly: false, defaultType: 'contactMail' });
      if (!deliveryObj)
        setDeliveryObject({ saveOnly: false, defaultType: 'contactMail' });
      return handleFeedback(t('No E-Mail address found'), 'error');
    }

    // If multiple emails, show selection dialog
    if (emails.length > 1) {
      setNameValueData({
        array: emails,
        nameKey: 'emailType',
        valueKey: 'emailAddress',
      });
    }

    // Use primary email or first available
    const primaryEmail = emails.find((e) => e.isPrimary);
    const deliveryMail = primaryEmail?.emailAddress || emails[0]?.emailAddress;

    emit({
      ...deliveryObject,
      id: data.id,
      saveOnly: false,
      defaultType: 'contactMail',
      deliveryMail,
      deliveryName,
    });
  }
  function handleSaveOnly() {
    emit({
      ...deliveryObject,
      saveOnly: true,
      defaultType: 'saveOnly',
    });
  }
  function handleCCChange(data) {
    if (!deliveryObject) return;
    emit({ ...deliveryObject, cc: data });
  }

  function handleContentChange(data) {
    if (!deliveryObject) return;
    emit({ ...deliveryObject, mailContent: data });
  }

  function handleAttachmentsChange(files) {
    const newAttachments = files?.length ? files : null;
    setAttachments(newAttachments);
    onSelect({ ...deliveryObject, attachments: newAttachments });
  }

  function handleEmailSelect(email) {
    emit({ ...deliveryObject, deliveryMail: email });
    setNameValueData({});
  }

  // feedback
  const [alertState, setAlertState] = useState({
    alertOpen: false,
    alertType: 'info',
    alertText: 'test',
  });
  function handleFeedback(text, type) {
    setAlertState({ alertOpen: true, alertText: text, alertType: type });
  }

  return (
    <Box className="flex flex-col gap-2 h-full overflow-auto">
      <Box className="flex flex-row items-start md:items-center gap-1 mt-4">
        <ToggleButtonGroup
          value={type}
          exclusive
          onChange={handleDelivertype}
          className="flex flex-col md:flex-row gap-1 flex-1"
        >
          {options.map((option) => (
            <ToggleButton
              key={option}
              value={optionMap[option].value}
              sx={{
                border: 'none',
                borderRadius: '5px!important',
                bgcolor: colors.bgInput,
              }}
            >
              <Box className="flex flex-row gap-2">
                {optionMap[option].icon}
                {optionMap[option].label}
              </Box>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        {allowAttachments && (
          <MailAttachmentsButton
            attachments={attachments}
            onChange={handleAttachmentsChange}
          />
        )}
      </Box>

      {allowAttachments && (
        <MailAttachments
          attachments={attachments}
          onChange={handleAttachmentsChange}
          disabled={type === 'saveOnly'}
        />
      )}

      {allowCC && type !== 'saveOnly' && (
        <MailCC
          onCCChange={handleCCChange}
          preSelected={deliveryObject || null}
        />
      )}
      {allowMailContent && type !== 'saveOnly' && (
        <MailContent
          onContentChange={handleContentChange}
          preSelected={deliveryObject || null}
          textBlockType={textBlockType}
        />
      )}
      <Box className="flex flex-col h-full w-full">
        {type === 'recipientMail' && (
          <RecipientMail
            onChange={handleRecipientMail}
            recipient={recipient}
            defaultKey={defaultRecipientMailKey}
            preSelected={deliveryObject}
          />
        )}
        {type === 'contactMail' && (
          <ContactMailSearch
            onSelect={handleContactMail}
            preSelected={deliveryObject}
          />
        )}
        {type === 'customMail' && (
          <CustomMail
            onChange={handleCustomMail}
            preSelected={deliveryObject}
          />
        )}
        {type === 'saveOnly' && <SaveOnly />}
      </Box>
      <ExoDialog open={Boolean(nameValueData.array)} limitWidth>
        <SelectFromNameValueArray
          label="Select email"
          array={nameValueData.array}
          nameKey={nameValueData.nameKey}
          valueKey={nameValueData.valueKey}
          onSelect={handleEmailSelect}
        />
      </ExoDialog>
      <Feedback state={alertState} setState={setAlertState} />
    </Box>
  );
};

const MailAttachmentsButton = ({ attachments, onChange }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  function handleSelect(files) {
    onChange(files);
    setOpen(false);
  }

  return (
    <>
      <Tooltip title={`${t('Attachments')} (max. 8 MB)`}>
        <IconButton
          onClick={() => setOpen(true)}
          sx={{
            borderRadius: '5px',
            bgcolor: colors.bgInput,
            height: '48px',
            width: '48px',
            flexShrink: 0,
          }}
        >
          <Badge
            badgeContent={attachments?.length || 0}
            color="info"
            invisible={!attachments?.length}
          >
            <AttachFileIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <ExoDialog open={open} onClose={() => setOpen(false)}>
        <ExoExplorer
          onSelect={handleSelect}
          onClose={() => setOpen(false)}
          multiSelect
          preSelected={attachments || []}
          maxItems={10}
          maxSizeKB={MAX_ATTACHMENT_SIZE_KB}
        />
      </ExoDialog>
    </>
  );
};

const MailAttachments = ({ attachments, onChange, disabled }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  if (!attachments?.length) return null;

  function handleRemove(id) {
    onChange(attachments.filter((file) => file.id !== id));
  }

  return (
    <Box className="flex flex-col gap-1">
      <Box
        className={`flex flex-row flex-wrap gap-1 ${
          disabled ? 'opacity-40' : ''
        }`}
      >
        {attachments.map((file) => (
          <Box
            key={file.id}
            className="flex items-center gap-2 pl-1 pr-0.5 py-0.5 rounded-md"
            sx={{ bgcolor: colors.bgInput, maxWidth: '240px' }}
          >
            <MediaPreview
              src={file.thumbnailUrl || file.temporaryUrl}
              mimeType={file.mimeType || undefined}
              size="24px"
            />
            <Typography fontSize={13} noWrap title={file.title || file.name}>
              {file.title || file.name}
            </Typography>
            <IconButton
              size="small"
              onClick={() => handleRemove(file.id)}
              sx={{
                opacity: 0.5,
                '&:hover': { opacity: 1, color: colors.error },
              }}
            >
              <ClearIcon sx={{ fontSize: '16px' }} />
            </IconButton>
          </Box>
        ))}
      </Box>
      {disabled && (
        <Typography fontSize={12} className="opacity-60">
          {t('Attachments are only sent with the e-mail delivery.')}
        </Typography>
      )}
    </Box>
  );
};

const MailCC = ({ onCCChange, preSelected }) => {
  const { t } = useTranslation();

  const [startData, setStartData] = useState(preSelected);

  // a CC list that arrives after mount (e.g. the employees proposed by the
  // settings) is shown as well; an unchanged list is left alone
  useEffect(() => {
    if (preSelected?.cc !== undefined && preSelected.cc !== startData?.cc)
      setStartData({ cc: preSelected.cc });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preSelected?.cc]);
  const fields = [
    {
      label: t('CC Mails'),
      key: 'cc',
      type: 'mailSelect',
    },
  ];

  function handleCCChange(data) {
    // the form reports once before its start data is applied
    if (data.cc === undefined) return;
    onCCChange(data.cc);
  }

  return (
    <ExoForm
      outerClassName="flex flex-col gap-4 w-full"
      sx={{ marginTop: '10px' }}
      fields={fields}
      noControls
      onChange={handleCCChange}
      startDataCollection={startData}
    />
  );
};

const MailContent = ({ onContentChange, preSelected, textBlockType }) => {
  const { t } = useTranslation();

  const [startData] = useState(preSelected);
  const fields = [
    {
      label: `${t('Additional Mail Content')} (${t('optional')})`,
      key: 'mailContent',
      type: 'richText',
      textBlockType: textBlockType,
    },
  ];

  function handleContentChange(data) {
    onContentChange(data.mailContent);
  }

  return (
    <ExoForm
      outerClassName="flex flex-col gap-4 w-full"
      sx={{ marginTop: '10px' }}
      fields={fields}
      noControls
      onChange={handleContentChange}
      startDataCollection={startData}
    />
  );
};

const RecipientMail = ({ recipient, defaultKey, onChange, preSelected }) => {
  const { t } = useTranslation();
  const [deliveryMail, setDeliveryMail] = useState(null);
  const [mailSettings, setMailSettings] = useState(null);

  function handleChange(value) {
    setDeliveryMail(value);
    onChange(value);
  }

  useEffect(() => {
    if (!recipient) {
      onChange(null);
      return;
    }

    let emailOptions = [];
    //Recipient has an array of emails
    if (Array.isArray(recipient.emails) && recipient.emails.length > 0) {
      emailOptions = recipient.emails.map((el) => ({
        onClick: () => handleChange(el.emailAddress),
        label: `${el.emailType || t('Email')} <${el.emailAddress}>`,
        emailAddress: el.emailAddress,
        isPrimary: el[defaultKey || 'isPrimary'],
      }));
    }
    // Recipient has a single email field (when creating employee contracts)
    else if (recipient.email) {
      emailOptions = [
        {
          onClick: () => handleChange(recipient.email),
          label: recipient.email,
          emailAddress: recipient.email,
          isPrimary: true,
        },
      ];
    }
    // If still no email found, clear states and notify parent
    if (!emailOptions.length) {
      onChange(null);
      setMailSettings(null);
      setDeliveryMail(null);
      return;
    }
    // Determine which email should be the default:
    // Primary email (if any)
    // Otherwise, the first email in the list
    let defaultMail =
      emailOptions.find((e) => e.isPrimary)?.emailAddress ||
      emailOptions[0].emailAddress;

    setDeliveryMail(defaultMail);
    onChange(defaultMail);
    setMailSettings(emailOptions);
  }, [recipient]);

  // If no email found show this
  if (!mailSettings || !deliveryMail) {
    return (
      <Box className="mt-4 flex items-center justify-center flex-col gap-4">
        <MailIcon color="error" sx={{ fontSize: '80px' }} />
        <Typography variant="h5">{t('No E-Mail address found')}</Typography>
      </Box>
    );
  }
  //  Display recipient info and selected email
  return (
    <Box>
      <Box className="mt-4 flex items-center justify-center flex-col gap-4">
        <MailIcon sx={{ fontSize: '80px' }} />
        <Box className="flex flex-col justify-center items-center gap-1">
          <Typography variant="h3" className="pb-4">
            {recipient &&
              (recipient.name
                ? recipient.name
                : recipient.firstName + ' ' + recipient.lastName)}
          </Typography>

          <Box className="flex items-center gap-2">
            <Typography variant="h5">{deliveryMail}</Typography>
            {mailSettings && mailSettings.length > 1 && (
              <SettingsPopover label={t('E-Mails')} settings={mailSettings} />
            )}
          </Box>
        </Box>
        <Alert
          severity="info"
          className="w-full"
          sx={{ flexDirection: 'row!important' }}
        >
          {t(
            'The document will be automatically sent by e-mail to the official e-mail address of the recipient.',
          )}
        </Alert>
      </Box>
    </Box>
  );
};

const CustomMail = ({ onChange, preSelected }) => {
  const { t } = useTranslation();
  const [custom, setCustom] = useState({ name: '', email: '' });

  function handleCustomMailChange(data) {
    setCustom(data);
    onChange(data);
  }

  useEffect(() => {
    if (
      !custom.email &&
      !custom.name &&
      preSelected &&
      preSelected.deliveryMail
    ) {
      handleCustomMailChange({
        email: preSelected.deliveryMail,
        name: preSelected.deliveryName,
      });
    }
  }, []);

  return (
    <Box className="mt-4 flex items-center justify-center flex-col gap-4">
      <TextField
        className="w-full"
        label={t('Recipient Name')}
        variant="outlined"
        onChange={(e) => {
          handleCustomMailChange({ ...custom, name: e.target.value });
        }}
        value={custom.name}
      />
      <TextField
        className="w-full"
        label={t('Mail')}
        variant="outlined"
        onChange={(e) => {
          handleCustomMailChange({ ...custom, email: e.target.value });
        }}
        value={custom.email}
      />
      <Alert
        severity="info"
        className="w-full"
        sx={{ flexDirection: 'row!important' }}
      >
        {t(
          'The document will be sent automatically by mail to the email address entered above.',
        )}
      </Alert>
    </Box>
  );
};

const SaveOnly = () => {
  const { t } = useTranslation();
  return (
    <Box className="mt-4 flex items-center justify-center flex-col gap-4">
      <CloudIcon sx={{ fontSize: '80px' }} />
      <Box className="flex flex-col justify-center items-center gap-1">
        <Typography variant="h3">{t('Save to the cloud only')}</Typography>
      </Box>
      <Alert
        severity="warning"
        className="w-full"
        sx={{ flexDirection: 'row!important' }}
      >
        {t(
          'The Document is exclusively created and stored in the cloud. An automatic delivery does not take place.',
        )}
      </Alert>
    </Box>
  );
};

const ContactMailSearch = ({ onSelect, preSelected }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (
      preSelected?.deliveryName &&
      preSelected?.defaultType === 'contactMail'
    ) {
      setSelected({
        name: preSelected.deliveryName,
        email: preSelected.deliveryMail,
      });
    }
  }, []);

  function handleSelection(data, type) {
    const nameMap = {
      company: (d) => d.nameShort || d.name,
      companyLead: (d) => d.nameShort || d.name,
      contact: (d) =>
        `${d.salutation || ''} ${d.firstName || ''} ${d.lastName || ''}`.trim(),
      personLead: (d) =>
        `${d.salutation || ''} ${d.firstName || ''} ${d.lastName || ''}`.trim(),
      user: (d) => `${d.firstName || ''} ${d.lastName || ''}`.trim(),
    };

    const name = nameMap[type] ? nameMap[type](data) : data.name || '';

    // Extract email
    let email = null;
    if (type === 'user') {
      email = data.email;
    } else if (Array.isArray(data.emails) && data.emails.length > 0) {
      const primary = data.emails.find((e) => e.isPrimary);
      email = primary?.emailAddress || data.emails[0]?.emailAddress;
    }

    const pictureUrl =
      data.picture?.temporaryUrl || data.logo?.thumbnailUrl || null;

    setSelected({ name, email, picture: pictureUrl, type });
    onSelect(data, type);
  }

  function handleCustomAdd(email) {
    setSelected({ name: email, email, type: 'custom' });
    onSelect(
      { email, emails: [{ emailType: 'custom', emailAddress: email }] },
      undefined,
    );
  }

  function handleClear() {
    setSelected(null);
    onSelect({});
  }

  return (
    <Box className="mt-4 flex flex-col gap-4">
      <ExoAutoCompleteSelector
        onSelectionChange={handleSelection}
        searchParams={[
          'companies',
          'contacts',
          'person_leads',
          'company_leads',
          'users',
        ]}
        onAdd={handleCustomAdd}
        label={t('Search recipient')}
      />

      {selected?.email ? (
        <Box className="flex items-center justify-center flex-col gap-4">
          <MailIcon sx={{ fontSize: '60px', color: colors.info }} />
          <Box className="flex flex-col items-center gap-2">
            <Box className="flex items-center gap-2">
              <ExoAvatar
                picture={selected.picture}
                name={selected.name}
                size="40px"
                type={
                  selected.type === 'company' || selected.type === 'companyLead'
                    ? 'company'
                    : 'contact'
                }
              />
              <Box className="flex flex-col">
                <Typography fontWeight={600}>{selected.name}</Typography>
                <Typography fontSize={13} className="opacity-60">
                  {selected.email}
                </Typography>
              </Box>
            </Box>
            <Chip
              label={t('Clear selection')}
              onDelete={handleClear}
              size="small"
              sx={{
                bgcolor: colors.bgInput,
                '& .MuiChip-deleteIcon:hover': { color: colors.error },
              }}
            />
          </Box>
          <Alert
            severity="info"
            className="w-full"
            sx={{ flexDirection: 'row!important' }}
          >
            {t(
              'The document will be automatically sent by e-mail to the selected recipient.',
            )}
          </Alert>
        </Box>
      ) : (
        <Box className="flex items-center justify-center flex-col gap-2 py-4 opacity-50">
          <ContactsIcon sx={{ fontSize: '50px' }} />
          <Typography fontSize={14}>
            {t('Search and select a recipient')}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SelectDelivery;
